import React, {CSSProperties, useMemo} from 'react'
import styled from 'styled-components'
import {WINDOW_MODE_MOBILE_WIDTH, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {getCurrencyValue, parseDate} from 'utils'
import {IconFirstBuy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {TransactionDetailContentRedesignedProps} from './TransactionDetailContentProps'
import {TransactionDetailContentHeader} from '../ContentHeader'
import {CopyInvoice} from '../CopyInvoice'

const StyledContainer = styled.div<CSSProperties>`
  display: flex;
  margin: ${convertUnit(25)};
  padding: ${convertUnit(25)} 0;
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  align-self: center;
  overflow: auto;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    flex-direction: column;
    padding: ${convertUnit(25)};
  }
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    min-height: 100%;
    width: calc(100% - ${convertUnit(50)});
    margin: 0;
  }
`

const StyledContentSection = styled.div<CSSProperties>`
  display: flex;
  flex-direction: column;
  margin: 0 ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin: 0;
    margin-top: ${({marginTop}) => marginTop};
  }
`

const StyledInfoContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-right: ${convertUnit(25)};
`

const StyledInfoItem = styled.div`
  width: 50%;
  margin-bottom: ${convertUnit(16)};
`

export default function TransactionDetailContentIncentive({
  stateData,
}: TransactionDetailContentRedesignedProps) {
  const {translate} = useTranslation()

  const {
    invoice_id,
    status: stateDataStatus,
    payment_at,
    transaction_total,
  } = stateData
  const transaction_type = 'Incentive'

  const renderContent = useMemo(
    () =>
      invoice_id && (
        <>
          <StyledContentSection width={'50%'}>
            <TransactionDetailContentHeader
              title={translate('giftShop:transactionType', {
                context: transaction_type.toUpperCase(),
              })}
              subTitle={translate('global:fotoyu')}
              statuses={[
                {
                  status: stateDataStatus,
                  count: 1,
                },
              ]}
              subtitleRightElement={
                <IconFirstBuy text={translate('giftShop:firstPromo')} />
              }
            />
            <StyledInfoContainer>
              {payment_at && (
                <>
                  <StyledInfoItem>
                    <Paragraph fontSize="m" fontWeight="medium">
                      {translate('giftShop:transactionPaymentTime')}
                    </Paragraph>
                    <Paragraph fontSize="m">
                      {(payment_at &&
                        parseDate(payment_at, 'MMMM D, YYYY, HH:mm')) ||
                        ''}
                    </Paragraph>
                  </StyledInfoItem>
                </>
              )}
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:invoiceID')}
                </Paragraph>
                <CopyInvoice invoice_id={invoice_id} context="single" />
              </StyledInfoItem>
              {transaction_type === 'Incentive' && (
                <>
                  <StyledInfoItem>
                    <Paragraph fontSize="m" fontWeight="medium">
                      {translate('giftShop:totalIncentive')}
                    </Paragraph>
                    <Paragraph color="primary_5" fontSize="m" fontWeight="bold">
                      {(transaction_total &&
                        getCurrencyValue(transaction_total)) ||
                        ''}
                    </Paragraph>
                  </StyledInfoItem>
                </>
              )}
            </StyledInfoContainer>
          </StyledContentSection>
        </>
      ),
    [invoice_id, translate, stateDataStatus, payment_at, transaction_total],
  )

  return <StyledContainer>{renderContent}</StyledContainer>
}
